import React from 'react';
import { Activity, Zap, Shield, TestTube2 } from 'lucide-react';
import '../styles/ScienceSection.css';

const ScienceSection = () => {
  return (
    <section className="science-section section-padding">
      <div className="container">
        <div className="science-header">
          <h2 className="heading-1">
            The <span className="accent-orange">Science</span> of Cordyceps
          </h2>
          <p className="body-large science-intro">
            From Traditional Use to Modern Research
          </p>
        </div>

        <div className="science-intro-text">
          <p className="body-regular">
            Over the last two decades, Cordyceps — particularly Cordyceps militaris — has become the subject of extensive scientific research across cell biology, sports science, immunology, and metabolic health.
          </p>
          <p className="body-regular">
            Modern interest focuses on its unique bioactive compounds, especially cordycepin (3'-deoxyadenosine), polysaccharides, nucleosides, and antioxidants — compounds that interact with the body at a cellular and metabolic level rather than acting as stimulants.
          </p>
        </div>

        <div className="science-image-section">
          <img 
            src="https://customer-assets.emergentagent.com/job_adapt-wellness/artifacts/42de1r9a_The-structural-difference-between-cordycepin-and-adenosine.png" 
            alt="Structural difference between cordycepin and adenosine"
            className="science-diagram"
          />
        </div>

        <div className="science-mechanisms">
          <h3 className="heading-2 text-center">How Cordyceps Works Inside the Body</h3>
          
          <div className="mechanism-grid">
            <div className="mechanism-card">
              <div className="mechanism-icon accent-orange">
                <Activity size={32} />
              </div>
              <h4 className="heading-3">Cellular Energy & ATP Production</h4>
              <p className="body-regular">
                Research suggests Cordyceps supports the body's ability to produce ATP (adenosine triphosphate) — the fundamental energy currency of cells.
              </p>
              <p className="body-regular">
                Instead of forcing energy (like caffeine), Cordyceps appears to:
              </p>
              <ul className="science-list">
                <li>Improve mitochondrial efficiency</li>
                <li>Support better oxygen utilization</li>
                <li>Reduce perceived fatigue during physical and mental exertion</li>
              </ul>
              <p className="body-small mechanism-note">
                This explains why Cordyceps is often described as providing clean, sustained energy without crashes or jitters.
              </p>
            </div>

            <div className="mechanism-card">
              <div className="mechanism-icon accent-orange">
                <Zap size={32} />
              </div>
              <h4 className="heading-3">Oxygen Utilization & Endurance</h4>
              <p className="body-regular">
                Multiple studies have examined Cordyceps' role in:
              </p>
              <ul className="science-list">
                <li>Enhancing oxygen uptake</li>
                <li>Supporting respiratory efficiency</li>
                <li>Improving endurance markers in physically active individuals</li>
              </ul>
              <p className="body-small mechanism-note">
                This aligns with both traditional use in high-altitude regions and modern interest from athletes and fitness communities.
              </p>
            </div>

            <div className="mechanism-card">
              <div className="mechanism-icon accent-orange">
                <TestTube2 size={32} />
              </div>
              <h4 className="heading-3">Adaptogenic Stress Support</h4>
              <p className="body-regular">
                Cordyceps is classified as an adaptogen, meaning it helps the body maintain balance under physical, mental, or environmental stress.
              </p>
              <p className="body-regular">
                Scientific observations indicate it may:
              </p>
              <ul className="science-list">
                <li>Help regulate stress-response pathways</li>
                <li>Support adrenal balance without overstimulation</li>
                <li>Aid recovery during periods of fatigue or overexertion</li>
              </ul>
              <p className="body-small mechanism-note">
                Unlike stimulants, adaptogens work by normalizing function, not overriding it.
              </p>
            </div>

            <div className="mechanism-card">
              <div className="mechanism-icon accent-orange">
                <Shield size={32} />
              </div>
              <h4 className="heading-3">Immune Modulation</h4>
              <p className="body-regular">
                Current research focuses on Cordyceps' immunomodulatory effects, meaning it helps regulate immune activity rather than aggressively stimulating it.
              </p>
              <p className="body-regular">
                Key findings suggest:
              </p>
              <ul className="science-list">
                <li>Support for innate immune responses</li>
                <li>Antioxidant activity that reduces oxidative stress</li>
                <li>Potential anti-inflammatory pathways</li>
              </ul>
              <p className="body-small mechanism-note">
                This distinction is important — Cordyceps supports immune balance, not immune overload.
              </p>
            </div>
          </div>
        </div>

        <div className="science-safety">
          <h3 className="heading-2">Safety, Toxicology & Long-Term Use</h3>
          <p className="body-regular">
            One of the strongest advantages of cultivated Cordyceps militaris is its safety profile.
          </p>
          <p className="body-regular">
            Scientific studies evaluating toxicity have consistently shown:
          </p>
          <ul className="science-list-inline">
            <li>No observed toxic effects at commonly studied doses</li>
            <li>No evidence of organ toxicity (liver or kidney)</li>
            <li>Good tolerance in both short-term and longer-term use</li>
          </ul>
          <div className="safety-highlight">
            <p className="body-large">Importantly:</p>
            <ul className="science-list">
              <li>Cultivated Cordyceps militaris avoids contamination risks associated with wild-harvested fungi</li>
              <li>Controlled cultivation eliminates exposure to heavy metals, pesticides, and microbial toxins</li>
              <li>Lab testing ensures purity, potency, and consistency</li>
            </ul>
            <p className="body-regular">
              This makes Cordyceps militaris suitable for regular wellness routines, not just occasional use.
            </p>
          </div>
        </div>

        <div className="science-quality">
          <h3 className="heading-2">Why Cultivation and Testing Matter</h3>
          <p className="body-large">
            Scientific research also highlights a critical truth: the benefits of Cordyceps depend heavily on how it is grown and processed.
          </p>
          <div className="quality-comparison">
            <div className="quality-column poor">
              <h4 className="heading-3">Poor-Quality Products May:</h4>
              <ul className="science-list">
                <li>Contain negligible active compounds</li>
                <li>Be diluted with fillers</li>
                <li>Carry contamination risks</li>
              </ul>
            </div>
            <div className="quality-column good">
              <h4 className="heading-3 accent-orange">High-Quality, Lab-Tested Cordyceps Ensures:</h4>
              <ul className="science-list">
                <li>Consistent cordycepin levels</li>
                <li>Verified safety</li>
                <li>Reliable functional benefits</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="science-conclusion">
          <h3 className="heading-2">Science Meets Responsibility</h3>
          <p className="body-large">
            Modern research does not treat Cordyceps as a miracle drug — and neither should consumers.
          </p>
          <p className="body-regular">
            Instead, it positions Cordyceps militaris as:
          </p>
          <div className="conclusion-points">
            <div className="point-item">
              <span className="accent-orange">A functional wellness ingredient</span>
            </div>
            <div className="point-item">
              <span className="accent-orange">A long-term support</span> for energy, endurance, and balance
            </div>
            <div className="point-item">
              <span className="accent-orange">A natural compound</span> best used as part of a consistent, mindful lifestyle
            </div>
          </div>
          <p className="body-large science-final">
            This is where science, tradition, and responsible wellness intersect.
          </p>
        </div>
      </div>
    </section>
  );
};

export default ScienceSection;